import apiFetch from "@/app/auth/apiFetch";
import type { NavigateFunction } from "react-router-dom";

const API_URL = import.meta.env.VITE_API_URL;

export type GeocodeCacheStatus = "resolved" | "country_only" | "miss";

/** Work-level heartbeat of a resolution run (sweeper / nightly backfill),
 * written by back/ in Redis — a K8s "Succeeded" alone cannot tell a
 * productive run from a no-op. */
export interface GeoPipelineRun {
  at?: string;
  attempted?: number;
  resolved?: number;
  quota_hit?: boolean;
  geo_resolved?: number;
  geo_pending_before?: number;
  candidates_seen?: number;
  candidates_updated?: number;
  duration_s?: number;
}

export interface GeoStats {
  geo_locations: {
    total: number;
    geocoded: number;
    /** Legacy all-inclusive count = pending + unnamed + exhausted. */
    missing: number;
    /** Name present, still retryable (waiting for quota or backoff). */
    pending?: number;
    /** No name from LinkedIn — un-geocodable until a later enrichment heals it. */
    unnamed?: number;
    /** Attempt budget spent — abandoned by the sweeper/backfill. */
    exhausted?: number;
  };
  geocode_cache: {
    total: number;
    hits: number;
    /** Legacy all-inclusive count = country_only + true_misses. */
    misses: number;
    /** Deterministic country-code short-circuits — successes, not failures. */
    country_only?: number;
    /** Provider affirmatively found nothing. */
    true_misses?: number;
    last_geocoded_at: string | null;
  };
  candidates: {
    total: number;
    with_geo_urn: number;
    with_coords: number;
    /** Candidates whose geocoded country contradicts the country code LinkedIn
     * returned for the same profile. Both come from one payload and cannot
     * legitimately disagree — anything above the historical residue is a live
     * geocoding regression (10 378 Paris candidates sat in Turkey unnoticed
     * from 2026-06-18 to 2026-09-09). */
    country_mismatch?: number;
  };
  pipeline?: {
    sweeper?: GeoPipelineRun | null;
    backfill?: GeoPipelineRun | null;
  };
}

export interface GeocodeCacheItem {
  raw_location: string;
  latitude: number | null;
  longitude: number | null;
  city: string | null;
  region: string | null;
  country: string | null;
  geocoded_at: string | null;
  resolved: boolean;
  status?: GeocodeCacheStatus;
  attempts?: number;
}

export interface GeocodeCacheResponse {
  items: GeocodeCacheItem[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export async function getGeoStats(navigate: NavigateFunction): Promise<GeoStats> {
  const res = await apiFetch(`${API_URL}/geo/stats`, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error(`Erreur ${res?.status ?? "?"} (statistiques géo)`);
  return res.json();
}

export async function listGeocodeCache(
  navigate: NavigateFunction,
  params: { page?: number; size?: number; q?: string; status?: string },
): Promise<GeocodeCacheResponse> {
  const qs = new URLSearchParams();
  qs.set("page", String(params.page ?? 1));
  qs.set("size", String(params.size ?? 25));
  if (params.q) qs.set("q", params.q);
  if (params.status && params.status !== "all") qs.set("status", params.status);
  const res = await apiFetch(
    `${API_URL}/geo/cache?${qs.toString()}`,
    { method: "GET" },
    navigate,
  );
  if (!res?.ok) throw new Error(`Erreur ${res?.status ?? "?"} (cache géocodage)`);
  return res.json();
}
